import React, { useState } from 'react';

import {
  PAYER_MODE,
  PAYER_MODE_OPTIONS,
  PAYER_TYPE,
  PAYER_TYPES,
  PAYER_TYPE_LABELS,
  PAYMENT_PERMISSIONS,
  ADMISSION_PERMISSIONS,
  canEditInsuranceAmounts,
  canEditApprovalRounds,
  isMoneyHidden,
} from '../../constants/admission';
import { usePermission } from '../../context/PermissionContext';
import { patchAdmission, patchAdmissionInsurance } from '../../services/api/admissionApi';
import { formatPaise } from '../../utils/money';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import { INSURER_REGISTER, TPA_REGISTER } from '../../constants/insurerTpaRegisters';
import {
  deriveInsuranceForm,
  buildInsurancePatchBody,
  computePatientBornePaise,
  computePatientPayEstimatePaise,
} from './insuranceModel';
import ApprovalRoundsList from './ApprovalRoundsList';
import RegisterCombobox from './RegisterCombobox';

const cardCls = 'rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4';
const sectionLbl = 'text-[11px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mt-4 mb-2';
const fieldGrid = 'grid gap-x-3.5 gap-y-3 sm:grid-cols-2';
const labelCls = 'block text-[11.5px] font-semibold text-slate-500 dark:text-slate-400 mb-1';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';

// S7 — ratified copy (stories.md § Copy), verbatim.
export const INSURANCE_NO_PERMISSION_REASON = 'Only the billing desk or an administrator can change insurance details.';
export const INSURANCE_LOCKED_NOTE = 'The discharge is complete. Change insurance details through a bill correction.';
export const LEAVING_CASHLESS_TITLE = 'Remove the cashless approval?';
export const LEAVING_CASHLESS_REASON_LABEL = 'Reason for the change';
export const LEAVING_CASHLESS_REASON_MISSING = 'Add a reason for the change.';
export const LEAVING_CASHLESS_CTA = 'Remove approval & save';
export const INSURANCE_UPDATED_TOAST = 'Insurance details updated.';
// FIX-14 — the two RATIFIED linkage helpers (business-payer-linkage.md), one
// under each of the two fields this card now owns. Verbatim, nothing else.
// Compliance nit (FIX-16): the helper must name the payer types the way the
// selector labels them, never the raw stored tokens.
export const PAYER_TYPE_HELPER = 'Payer type follows the claim mode for Cash/TPA; Govt scheme and Credit / corporate are set manually.';
export const CLAIM_MODE_HELPER = 'Cashless sets payer type to TPA; Self-pay or Reimbursement resets it to Cash (only if currently TPA).';
export const PAYER_TYPE_LABEL = 'Payer type';
export const PAYER_TYPE_UNSET = 'No payer set';
export const leavingCashlessBody = (amountText) =>
  `${amountText} approved by the insurer will be reversed from this bill. The amount payable by the patient increases by the same amount.`;
export const insuranceReversedToast = (amountText) =>
  `${INSURANCE_UPDATED_TOAST} ${amountText} reversed from the bill.`;

/**
 * PayerInsuranceCard (Wave-3 FE-1; approval rounds added TPA-PREAUTH/
 * tpa-bill S2) — the "Payer & insurance" card in the admission workspace's
 * Details tab (mock1-capture.html), next to the existing Attender/Plan &amp;
 * estimate cells. Captures the owner-locked 3-way `payer_mode` (Cash/
 * Cashless/Reimbursement) — ORTHOGONAL to the existing `payer_type` chip
 * shown here unchanged for context — plus the claim-identity fields and
 * (cashless only) co-pay + disallowed. PATCHes via the existing
 * `patchAdmissionInsurance` (C-1 frozen contract); `onSaved` is the parent's
 * existing "refresh the admission" callback (reused, not a new one — the
 * same one discharge actions use, and the same one `ApprovalRoundsList`
 * below calls after every round add/edit/remove). `onSaved` now also RECEIVES
 * the PATCH response body so the parent can mirror the server-derived payer
 * fields with no refetch (FIX-14) — callers ignoring the argument are
 * unaffected.
 *
 * FIX-14 — the card also owns the `payer_type` selector at its top (it used to
 * be editable ONLY on the Admit form, so from Details there was no way to
 * reach GOVT). `payer_type` and `payer_mode` are LINKED and that linkage is
 * SERVER-OWNED (build-plan §C8): this card sends ONLY the field the desk
 * touched and reads the derived partner off the RESPONSE (`savedRow` below).
 * The mapping table is deliberately NOT mirrored client-side.
 *
 * No separate "apply to bill" affordance — applying is discharge-initiation,
 * handled by the discharge dialog elsewhere (owner ruling). Co-pay/disallowed
 * go read-only ("Confirmed" chip) once the stay passes discharge-initiation
 * (D4, `canEditInsuranceAmounts`); the claim-identity fields and the mode
 * itself stay editable while the viewer holds ADMIT_PATIENT.
 *
 * "Insurer-approved amount" is NO LONGER an input here (TPA-PREAUTH/tpa-bill
 * C1) — it is SERVER-DERIVED from the `ApprovalRoundsList` below ("Approved
 * so far" is read-only, single source of truth). Adding/editing/removing a
 * round is gated on `COLLECT_DEPOSIT` specifically (stories S2 AC8/AC-NEG),
 * independent of this card's own broader `canEdit`.
 */
export default function PayerInsuranceCard({ admission, canEdit, onSaved }) {
  const a = admission || {};
  const admissionId = a.admission_id;
  const [form, setForm] = useState(() => deriveInsuranceForm(a));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // The ratified success sentence to show ('' / null = none) — plain update or
  // the reversal variant naming the amount that left the bill.
  const [saved, setSaved] = useState(null);
  // FIX-14 — the payer pair as the LAST SAVE RESPONSE reported it, merged over
  // the admission prop: that is how the server-derived partner field reaches
  // this card, by local merge off the response body and NEVER a refetch (the
  // identical `deathOverride` idiom AdmissionWorkspace uses). Deliberately
  // NARROW — only the two fields this card writes. Merging the whole response
  // body would mask every LATER prop update for the keys it happens to carry
  // (an approval round added below, a deposit collected elsewhere), which is a
  // staleness bug, not a re-sync.
  const [savedRow, setSavedRow] = useState(null);
  const row = savedRow ? { ...a, ...savedRow } : a;

  // S7 — the whole card is editable through DISCHARGE_INITIATED /
  // BILLING_CLEARANCE and locks only at a TERMINAL status (the server's
  // `409 INSURANCE_LOCKED` window, mirrored by the ONE shared helper).
  // admission-access — money withheld (`money_visible === false`): the card is
  // the read-only view (payer type, claim mode, insurer name) whatever the
  // viewer's slugs say; the amounts, approval rounds and preview never render.
  const moneyHidden = isMoneyHidden(a);
  const amountsEditable = canEdit && !moneyHidden && canEditInsuranceAmounts(a.status);
  const editable = amountsEditable;
  // Approval-rounds write tier is COLLECT_DEPOSIT specifically (stories S2
  // AC8/AC-NEG) — narrower than this card's own `canEdit` (ADMIT_PATIENT OR
  // COLLECT_DEPOSIT), so it is checked independently here.
  const hasCollectDeposit = usePermission(PAYMENT_PERMISSIONS.COLLECT_DEPOSIT);
  const roundsEditable = canEdit && hasCollectDeposit && canEditApprovalRounds(a.status);
  // FIX-14 — `payer_type` rides the GENERAL admission PATCH (the insurance
  // route's body is `extra="forbid"` and carries insurance fields only), and
  // that route is gated ADMIT_PATIENT/DISCHARGE_PATIENT — NOT the biller's
  // COLLECT_DEPOSIT. Checked independently, same posture as the rounds tier
  // above, so a COLLECT_DEPOSIT-only biller sees the selector read-only WITH
  // its reason instead of a 403 on click. Permission model unchanged.
  const hasAdmitPatient = usePermission(ADMISSION_PERMISSIONS.ADMIT_PATIENT);
  const typeEditable = editable && hasAdmitPatient;
  const set = (field) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [field]: v }));
    setSaved(null);
  };
  // Same update as `set` above but for RegisterCombobox, which hands back
  // the plain string directly (not a change event) — insurer_name/tpa_name
  // stay plain strings either way (no data-model change).
  const setName = (field) => (v) => {
    setForm((f) => ({ ...f, [field]: v }));
    setSaved(null);
  };
  const pickMode = (mode) => {
    if (!editable) return;
    setForm((f) => ({ ...f, payer_mode: mode }));
    setSaved(null);
    setError(null);
  };

  const patientBornePaise = computePatientBornePaise(form.copay, form.disallowed);
  // "Approved so far" is SERVER-COMPUTED (C1 §API CONTRACT) — read straight
  // off the admission row, never re-derived from the rounds list here.
  const approvedPaise = row.insurer_approved_paise || 0;
  // S7 AC2 — LEAVING Cashless with insurer money already on the ledger: that
  // money is reversed by the save, so the desk must see the exact amount and
  // give a reason first. ONE predicate, TWO doors (the claim-mode control and
  // — FIX-14 — the payer-type selector's CASH option, which the server maps to
  // mode NONE): `cashlessMoneyOnLedger` is the single source both read, off
  // the LAST SERVER STATE (`row`), never the unsaved form.
  const cashlessMoneyOnLedger = row.payer_mode === PAYER_MODE.CASHLESS && approvedPaise > 0;
  const leavingCashless = cashlessMoneyOnLedger && form.payer_mode !== PAYER_MODE.CASHLESS;
  const reversalAmountText = formatPaise(approvedPaise);
  const [confirmingReversal, setConfirmingReversal] = useState(false);
  const [reversalReason, setReversalReason] = useState('');
  // The payer type awaiting the reversal confirm ('' / null = none). Also what
  // the selector shows while the modal is open, so Cancel reverts to the saved
  // value with no second piece of editor state to keep in sync.
  const [pendingPayerType, setPendingPayerType] = useState(null);

  // FIX-14 — the ONE place a save response lands. The server owns the
  // `payer_type` ⇄ `payer_mode` derivation (§C8), so the partner field is
  // taken from the RESPONSE and merged locally; the claim-mode control (which
  // holds its own editor state) follows the same body. No refetch, no
  // client-side mapping table.
  const applySaveResponse = (data) => {
    if (!data) return;
    const next = {};
    if (data.payer_type != null) next.payer_type = data.payer_type;
    if (data.payer_mode != null) next.payer_mode = data.payer_mode;
    if (!Object.keys(next).length) return;
    setSavedRow((cur) => ({ ...cur, ...next }));
    // The claim-mode control holds its own editor state — point it at the
    // value the server actually stored.
    if (next.payer_mode) setForm((f) => ({ ...f, payer_mode: next.payer_mode }));
  };

  // ONE write path for every save (plain, or the confirmed reversal) —
  // single-flight on `busy`, so a double-click can never fire two PATCHes.
  const commit = async (reason) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const body = buildInsurancePatchBody(form);
    // C7 — the reversal rides the EXISTING delta-settlement path; `reason` is
    // the same top-level key the rounds decrease already sends.
    if (reason) body.reason = reason;
    const res = await patchAdmissionInsurance(admissionId, body);
    setBusy(false);
    if (res.success) {
      setConfirmingReversal(false);
      setReversalReason('');
      applySaveResponse(res.data);
      setSaved(reason && leavingCashless ? insuranceReversedToast(reversalAmountText) : INSURANCE_UPDATED_TOAST);
      if (typeof onSaved === 'function') onSaved(res.data);
    } else {
      setError(res.error || 'Failed to save insurance details.');
    }
  };

  // FIX-14 — the payer-type write. A SINGLE field on the GENERAL admission
  // PATCH (the insurance route forbids it); the server derives the claim mode
  // from it and returns both. Single-flight on the same `busy` flag as the
  // insurance save, so the two doors can never overlap.
  const commitPayerType = async (value, reason) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const body = { payer_type: value };
    // §C8 — post-initiate a CASH pick that reverses insurer money needs the
    // SAME reason the leaving-Cashless mode change sends (REASON_REQUIRED).
    if (reason) body.reason = reason;
    const res = await patchAdmission(admissionId, body);
    setBusy(false);
    if (res.success) {
      setConfirmingReversal(false);
      setReversalReason('');
      setPendingPayerType(null);
      applySaveResponse(res.data);
      setSaved(reason ? insuranceReversedToast(reversalAmountText) : INSURANCE_UPDATED_TOAST);
      if (typeof onSaved === 'function') onSaved(res.data);
    } else {
      setPendingPayerType(null);
      setError(res.error || 'Failed to save insurance details.');
    }
  };

  const pickPayerType = (value) => {
    if (!typeEditable || busy) return;
    if (!value || value === row.payer_type) return;
    setSaved(null);
    setError(null);
    // The confirm (with its REQUIRED reason) stands between the desk and the
    // reversal — never a native dialog, never a silent write.
    if (value === PAYER_TYPE.CASH && cashlessMoneyOnLedger) {
      setPendingPayerType(value);
      setReversalReason('');
      setConfirmingReversal(true);
      return;
    }
    commitPayerType(value, null);
  };

  const handleSave = async () => {
    if (busy || !editable) return;
    setError(null);
    const body = buildInsurancePatchBody(form);
    if (form.payer_mode === PAYER_MODE.CASHLESS && (!body.insurer_name || !body.tpa_name || !body.policy_no)) {
      setError('Insurer name, TPA name and policy number are required.');
      return;
    }
    // The confirm (with its REQUIRED reason) stands between the desk and the
    // reversal — never a native dialog, never a silent write.
    if (leavingCashless) {
      setReversalReason('');
      setConfirmingReversal(true);
      return;
    }
    await commit(null);
  };
  const patientPayPaise = computePatientPayEstimatePaise(row.balance_paise, String(approvedPaise / 100));
  const claimIdentityBlank = !form.insurer_name || !form.tpa_name || !form.policy_no;

  // ── Read-only view — TWO reasons, each stating its own (never a silent
  // no-op): the viewer lacks the insurance-edit permission, or the stay is
  // CLOSED (terminal) and the correction rail owns it (S7 AC5/AC-NEG). The
  // edit affordance is ABSENT, not disabled. ──────────────────────────────
  if (!editable) {
    return (
      <div className={cardCls} data-testid="payer-insurance-card-readonly">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-bold text-slate-800 dark:text-white">Payer &amp; insurance</span>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300" data-testid="payer-type-chip">
            {PAYER_TYPE_LABELS[row.payer_type] || row.payer_type || PAYER_TYPE_UNSET}
          </span>
        </div>
        <div className="text-[12.5px] text-slate-600 dark:text-slate-300 mt-2">
          Claim mode: <b className="text-slate-900 dark:text-white">{PAYER_MODE_OPTIONS.find((m) => m.value === form.payer_mode)?.label || 'Cash / self-pay'}</b>
        </div>
        {form.payer_mode !== PAYER_MODE.NONE && (
          <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">
            {form.insurer_name || 'No insurer recorded'}{form.policy_no ? ` · Policy ${form.policy_no}` : ''}
          </div>
        )}
        <div className="text-[11px] italic text-slate-400 dark:text-slate-500 mt-2" data-testid="payer-insurance-readonly-reason">
          {canEdit && !moneyHidden ? INSURANCE_LOCKED_NOTE : INSURANCE_NO_PERMISSION_REASON}
        </div>
      </div>
    );
  }

  return (
    <div className={cardCls} data-testid="payer-insurance-card">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-bold text-slate-800 dark:text-white">Payer &amp; insurance</span>
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300" data-testid="payer-type-chip">
          {PAYER_TYPE_LABEL}: {PAYER_TYPE_LABELS[row.payer_type] || row.payer_type || PAYER_TYPE_UNSET}
        </span>
      </div>
      <div className="text-[12px] text-slate-500 dark:text-slate-400 mb-3">
        Set the claim mode and capture insurer details. Approval rounds can be recorded until the discharge is completed.
      </div>

      {/* FIX-14 — the payer type, ABOVE the claim mode: one card owns both
          linked fields. Saves on pick (single-flight on `busy`); the server
          derives the claim mode and this card re-reads BOTH off the response.
          Disabled states say why — never a silent no-op, never a 403 on
          click. */}
      <div className={sectionLbl}>{PAYER_TYPE_LABEL}</div>
      <div className="sm:max-w-xs">
        <select
          id="payer-type"
          className={inputCls}
          value={pendingPayerType || row.payer_type || ''}
          onChange={(e) => pickPayerType(e.target.value)}
          disabled={!typeEditable || busy}
          aria-label={PAYER_TYPE_LABEL}
          data-testid="payer-type-select"
        >
          {/* An admission with no payer class recorded shows exactly that —
              never a fabricated "Cash" that reads as a real answer. */}
          {!row.payer_type && <option value="">{PAYER_TYPE_UNSET}</option>}
          {PAYER_TYPES.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5" data-testid="payer-type-helper">
        {/* COPY: RATIFIED verbatim — business-payer-linkage.md */}
        {PAYER_TYPE_HELPER}
      </div>
      {!typeEditable && (
        <div className="text-[11px] italic text-slate-400 dark:text-slate-500 mt-1" data-testid="payer-type-disabled-reason">
          {INSURANCE_NO_PERMISSION_REASON}
        </div>
      )}

      <div className={sectionLbl}>Claim mode</div>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {PAYER_MODE_OPTIONS.map((m) => {
          const on = form.payer_mode === m.value;
          return (
            <button
              key={m.value}
              type="button"
              onClick={() => pickMode(m.value)}
              data-testid={`payer-mode-${m.value}`}
              aria-pressed={on}
              className={`text-left rounded-lg border px-3 py-2.5 ${on
                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30'
                : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-teal-400'}`}
            >
              <div className={`text-[12.5px] font-bold flex items-center gap-2 ${on ? 'text-teal-800 dark:text-teal-200' : 'text-slate-800 dark:text-slate-100'}`}>
                <span className={`inline-block w-3 h-3 rounded-full border-2 ${on ? 'border-teal-500 bg-teal-500' : 'border-slate-400 dark:border-slate-500'}`} />
                {m.label}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-snug">{m.sub}</div>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5" data-testid="claim-mode-helper">
        {/* COPY: RATIFIED verbatim — business-payer-linkage.md */}
        {CLAIM_MODE_HELPER}
      </div>

      {form.payer_mode === PAYER_MODE.NONE && (
        <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-3">
          No insurance capture needed for a cash / self-pay admission.
        </div>
      )}

      {form.payer_mode !== PAYER_MODE.NONE && (
        <>
          <div className={sectionLbl}>Insurer &amp; claim</div>
          <div className={fieldGrid}>
            <div>
              <label className={labelCls} htmlFor="ins-insurer-name">Insurer name</label>
              <RegisterCombobox
                id="ins-insurer-name"
                testId="ins-insurer-name"
                value={form.insurer_name}
                onChange={setName('insurer_name')}
                options={INSURER_REGISTER}
                placeholder="e.g. Star Health"
              />
            </div>
            {/* Screen E — REIMBURSEMENT hides the TPA (the patient claims
                later; no third party settles here), CASHLESS requires it. */}
            {form.payer_mode === PAYER_MODE.CASHLESS && (
              <div>
                <label className={labelCls} htmlFor="ins-tpa-name">TPA name</label>
                <RegisterCombobox
                  id="ins-tpa-name"
                  testId="ins-tpa-name"
                  value={form.tpa_name}
                  onChange={setName('tpa_name')}
                  options={TPA_REGISTER}
                  placeholder="e.g. Medi Assist"
                />
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="ins-policy-no">Policy no.</label>
              <input id="ins-policy-no" className={inputCls} value={form.policy_no} onChange={set('policy_no')} data-testid="ins-policy-no" />
            </div>
            <div>
              <label className={labelCls} htmlFor="ins-claim-no">Pre-auth / claim no.</label>
              <input id="ins-claim-no" className={inputCls} value={form.claim_no} onChange={set('claim_no')} data-testid="ins-claim-no" />
            </div>
          </div>
        </>
      )}

      {form.payer_mode === PAYER_MODE.CASHLESS && claimIdentityBlank && (
        <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5" data-testid="ins-claim-identity-hint">
          {/* COPY: Business-ratified (AC-I8) */}
          Insurer, TPA and policy number print on the TPA Bill.
        </div>
      )}

      {form.payer_mode === PAYER_MODE.CASHLESS && (
        <>
          <div className={sectionLbl}>Approval rounds</div>
          <div className="flex items-baseline justify-between rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/30 px-3.5 py-2.5 mb-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300">
              {/* COPY: verbatim — stories S2 AC-I4 */}
              Approved so far
            </span>
            <span className="text-[17px] font-extrabold text-slate-900 dark:text-white tabular-nums" data-testid="ins-approved-so-far">
              {formatPaise(approvedPaise)}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500 mb-3">
            {/* COPY: verbatim — stories S2 AC-I4 */}
            Calculated from the approval rows below.
          </div>
          <ApprovalRoundsList admission={row} canEdit={roundsEditable} onSaved={onSaved} />

          <div className={sectionLbl}>Amounts</div>
          <div className={fieldGrid}>
            <div>
              <label className={labelCls} htmlFor="ins-copay">Co-pay (₹)</label>
              <input id="ins-copay" type="number" min="0" step="0.01" className={inputCls} value={form.copay} onChange={set('copay')} disabled={!amountsEditable} data-testid="ins-copay" />
            </div>
            <div>
              <label className={labelCls} htmlFor="ins-disallowed">Disallowed (₹)</label>
              <input id="ins-disallowed" type="number" min="0" step="0.01" className={inputCls} value={form.disallowed} onChange={set('disallowed')} disabled={!amountsEditable} data-testid="ins-disallowed" />
            </div>
            <div>
              <label className={labelCls}>Patient-borne (co-pay + disallowed)</label>
              <div className="w-full px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-sm tabular-nums" data-testid="ins-patient-borne">
                {formatPaise(patientBornePaise)}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 p-3 mt-4" data-testid="ins-preview">
            <div className="text-[10.5px] font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300 mb-1.5">
              Patient-payable preview · live
            </div>
            <div className="flex justify-between text-[12.5px] text-slate-700 dark:text-slate-200 py-0.5">
              <span>Current balance</span>
              <span className="tabular-nums">{formatPaise(a.balance_paise)}</span>
            </div>
            <div className="flex justify-between text-[12.5px] text-teal-700 dark:text-teal-300 py-0.5">
              <span>Less: Insurer-approved (TPA)</span>
              <span className="tabular-nums">− {formatPaise(approvedPaise)}</span>
            </div>
            <div className="flex justify-between text-[15px] font-extrabold text-slate-900 dark:text-white border-t border-slate-300 dark:border-slate-600 mt-1.5 pt-2">
              <span>Patient will pay (estimate)</span>
              <span className="tabular-nums" data-testid="ins-patient-pay">{formatPaise(patientPayPaise)}</span>
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
              {/* COPY: Business-ratified */}
              Reflects <b>{formatPaise(patientBornePaise)}</b> co-pay + disallowed per policy terms — this is the patient's share shown above, not a second deduction.
            </div>
          </div>
        </>
      )}

      {form.payer_mode === PAYER_MODE.REIMBURSEMENT && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 mt-4 text-[12px] text-slate-600 dark:text-slate-300">
          <b className="text-slate-800 dark:text-slate-100">Patient pays the full invoice.</b> Claim info above is recorded so the patient can file for reimbursement with the insurer — it does not reduce what they owe here.
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12px] p-2.5" data-testid="ins-error">
          {error}
        </div>
      )}
      {saved && !error && (
        <div className="mt-3 rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[12px] p-2.5" data-testid="ins-saved">
          {saved}
        </div>
      )}

      <div className="mt-3">
        <button type="button" className={btnPrimary} onClick={handleSave} disabled={busy} data-testid="ins-save">
          {busy ? 'Saving…' : 'Save insurance details'}
        </button>
      </div>

      {/* S7 AC2 — leaving Cashless with money on the ledger. The in-app
          ConfirmationModal (never a native dialog) names the EXACT amount the
          card already shows and holds a REQUIRED reason; the CTA stays
          disabled until it is non-empty (the server's own `REASON_REQUIRED`
          422 is the backstop). FIX-14 — the SAME modal serves both doors: the
          claim-mode save and a payer-type pick of Cash (which the server maps
          to mode NONE); `pendingPayerType` says which one is confirming. */}
      {confirmingReversal && (
        <ConfirmationModal
          isOpen
          variant="danger"
          title={LEAVING_CASHLESS_TITLE}
          message={(
            <div className="space-y-2 text-left">
              <div data-testid="ins-reversal-body">{leavingCashlessBody(reversalAmountText)}</div>
              <label className={labelCls} htmlFor="ins-reversal-reason">{LEAVING_CASHLESS_REASON_LABEL}</label>
              <textarea
                id="ins-reversal-reason"
                className={inputCls}
                rows={2}
                value={reversalReason}
                onChange={(e) => setReversalReason(e.target.value)}
                data-testid="ins-reversal-reason"
              />
              {!reversalReason.trim() && (
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400" data-testid="ins-reversal-reason-missing">
                  {LEAVING_CASHLESS_REASON_MISSING}
                </div>
              )}
            </div>
          )}
          ctaLabel={busy ? 'Saving…' : LEAVING_CASHLESS_CTA}
          ctaDisabled={busy || !reversalReason.trim()}
          cancelLabel="Cancel"
          onConfirm={() => (pendingPayerType
            ? commitPayerType(pendingPayerType, reversalReason.trim())
            : commit(reversalReason.trim()))}
          onCancel={() => {
            if (busy) return;
            setConfirmingReversal(false);
            setPendingPayerType(null);
          }}
        />
      )}
    </div>
  );
}
